import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useInteractionManager, useWebCrafter } from '../../hooks/useWebCrafter.jsx'
import { debugLog, debugError } from '../../utils/debug'
import { PositionalAudioHelper } from 'three/examples/jsm/helpers/PositionalAudioHelper'

export function Audio3D(props)
{
  const {
    url,
    volume = 1,
    sourceObject = null,
    setAudioAnalyser,
    fftSize = 128,
    ...rest
  } = props

  const [ speaker, setSpeaker ] = useState()
  const { isDebugMode } = useWebCrafter()
  const camera = useThree(state => state.camera)
  const interactionManager = useInteractionManager()
  const [ callbacks, setCallbacks ] = useState({})

  const audioRef = useRef(document.createElement('audio'))

  // Create audio element
  const audio = useMemo(() =>
  {
    if (!url) return null
    if (audioRef.current?.src === url) return audioRef.current

    const a = audioRef.current
    if (!a) return null

    a.src = url
    a.preload = 'auto'
    a.crossOrigin = 'Anonymous'
    a.loop = false
    a.autoplay = false
    a.muted = false
    a.volume = volume

    return a
  }, [ url, audioRef.current ])

  const toggleAudio = useCallback(() =>
  {
    if (!audio) return

    if (audio.paused)
    {
      if (speaker?.context?.state === 'suspended')
      {
        speaker.context.resume()
      }
      audio.play().catch(err => debugError('[Audio3D] Playback failed:', err))
    } else
    {
      audio.pause()
    }
  }, [ audio, speaker ])

  // Subscribe to interaction manager audio triggers
  useEffect(() =>
  {
    if (!interactionManager) return

    const handleTrigger = (name) =>
    {
      if (sourceObject && name === sourceObject.name)
      {
        toggleAudio()
      }
    }

    const unsubscribe = interactionManager.subscribeToAudioTrigger(handleTrigger)
    return () => unsubscribe()
  }, [ sourceObject, toggleAudio, interactionManager ])

  useEffect(() =>
  {
    if (!speaker) return
    speaker.setVolume(volume)
  }, [ volume, speaker ])

  // Setup audio and callbacks
  useEffect(() =>
  {
    if (!audio) return

    // Setup speaker if not already created
    if (!speaker)
    {
      const listener = camera.children.find(c => c.type === 'AudioListener')
      if (!listener) return

      const isPositional = sourceObject?.userData?.mediaAudioPosition === 'Positional'
      const speak = isPositional ? new THREE.PositionalAudio(listener) : new THREE.Audio(listener)
      speak.setMediaElementSource(audio)
      speak.setVolume(volume)

      if (isPositional && isDebugMode)
      {
        speak.setRefDistance(1)
        const helper = new PositionalAudioHelper(speak, 1)
        helper.visible = true
        helper.position.copy(sourceObject.position)
        helper.rotation.copy(sourceObject.rotation)
        helper.scale.set(sourceObject.scale.x * 10, sourceObject.scale.y * 10, sourceObject.scale.z * 10)
        helper.name = 'webcrafter-audio-helper'
        helper.renderOrder = -1
        speak.add(helper)
      }

      if (setAudioAnalyser)
      {
        setAudioAnalyser(new THREE.AudioAnalyser(speak, fftSize))
      }

      setSpeaker(speak)
    }

    // Setup callbacks based on trigger type
    const mediaTrigger = sourceObject?.userData?.mediaTrigger
    const tempCallbacks = {}

    if (mediaTrigger === 'Looping')
    {
      audio.loop = true
      audio.play().catch(e => debugError('[Audio3D] Auto-play failed:', e))
    } else if (mediaTrigger === 'OnSelect')
    {
      tempCallbacks.onClick = () => toggleAudio()
    } else if (mediaTrigger === 'OnPointerExit' || mediaTrigger === 'OnPointerEnter')
    {
      tempCallbacks.onPointerEnter = () => audio.play().catch(e => debugError('[Audio3D] Play failed:', e))
      tempCallbacks.onPointerLeave = () => audio.pause()
    }

    setCallbacks(tempCallbacks)
  }, [ speaker, audio, url, sourceObject, camera, volume, toggleAudio, setAudioAnalyser, fftSize, isDebugMode ])

  // Cleanup on unmount
  useEffect(() =>
  {
    return () =>
    {
      if (speaker)
      {
        if (speaker.isPlaying) speaker.stop()
        speaker.disconnect()
        setSpeaker(undefined)
      }
      if (audio)
      {
        audio.pause()
        audio.remove()
      }
    }
  }, [])

  return (
    <group rotation={[ -Math.PI / 2, 0, 0 ]}>
      <primitive object={sourceObject.clone()} visible={false} {...callbacks} {...rest}>
        {speaker && <primitive {...rest} object={speaker} />}
      </primitive>
    </group>
  )
}
